/* eslint-disable */
import React, { useCallback, useRef, useState } from 'react';
import './index.less';
import './index.en.less';

export interface IRiskCarouselItem {
  id?: number;
  text?: string;
}

export interface IMisconceptionRiskCarouselProps {
  title?: string;
  tips?: string;
  items?: IRiskCarouselItem[];
  id?: string;
  onComplete?: () => void;
}

const OFFSET_REM: Record<number, number> = { 1: 19.8, 2: 31.3 };

const MisconceptionRiskCarousel: React.FunctionComponent<
  IMisconceptionRiskCarouselProps
> = ({ title, tips, items = [], id, onComplete }) => {
  const n = items.length;
  const [active, setActive] = useState(0);
  const [dragging, setDragging] = useState(false);
  const completedRef = useRef(false);
  const dragRef = useRef({ startX: 0, dragging: false, moved: 0 });

  const markComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  }, [onComplete]);

  const goTo = useCallback(
    (idx: number) => {
      if (!n) return;
      setActive(((idx % n) + n) % n);
      markComplete();
    },
    [n, markComplete]
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  // 循环偏移：以当前居中卡片为 0，左右两侧依次 -1/-2、+1/+2
  const getOffset = useCallback(
    (i: number) => {
      const half = Math.floor(n / 2);
      let offset = i - active;
      if (offset > half) offset -= n;
      else if (offset < -half) offset += n;
      return offset;
    },
    [active, n]
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragRef.current = { startX: e.clientX, dragging: true, moved: 0 };
    setDragging(true);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.dragging) return;
    dragRef.current.moved = e.clientX - dragRef.current.startX;
  }, []);

  const onPointerUp = useCallback(() => {
    const { dragging: wasDragging, moved } = dragRef.current;
    dragRef.current.dragging = false;
    setDragging(false);
    if (!wasDragging) return;
    // 选中文字时不触发滑动切卡，避免干扰复制
    if (window.getSelection()?.toString()) return;
    if (moved <= -40) next();
    else if (moved >= 40) prev();
  }, [next, prev]);

  const onCardClick = useCallback(
    (i: number) => {
      if (Math.abs(dragRef.current.moved) > 5) return;
      if (window.getSelection()?.toString()) return;
      if (getOffset(i) !== 0) goTo(i);
    },
    [getOffset, goTo]
  );

  if (!n) return null;

  return (
    <div
      className={`misconception-risk-carousel${dragging ? ' is-dragging' : ''}`}
      id={id}
    >
      {title && <h2 className="misconception-risk-carousel-title">{title}</h2>}
      {tips && <p className="misconception-risk-carousel-tips">{tips}</p>}
      <div className="misconception-risk-carousel-shell">
        <button
          type="button"
          className="misconception-risk-carousel-arrow misconception-risk-carousel-arrow--prev"
          onClick={prev}
          aria-label="Previous"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div
          className="misconception-risk-carousel-viewport"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <div className="misconception-risk-carousel-stage">
            {items.map((item, i) => {
              const offset = getOffset(i);
              const abs = Math.abs(offset);
              const visible = abs <= 2;
              const tier =
                abs === 0 ? 'is-center' : abs === 1 ? 'is-near' : 'is-far';
              const tx =
                abs === 0 ? 0 : (offset > 0 ? 1 : -1) * OFFSET_REM[abs];
              const style: React.CSSProperties = {
                transform: `translate(-50%, -50%) translateX(${tx}rem)`,
                zIndex: abs === 0 ? 30 : abs === 1 ? 20 : 10,
                opacity: visible ? undefined : 0,
                pointerEvents: visible ? undefined : 'none',
              };
              return (
                <div
                  key={item.id ?? i}
                  className={`misconception-risk-carousel-card ${tier}`}
                  style={style}
                  onClick={() => onCardClick(i)}
                >
                  <div className="misconception-risk-carousel-card-index">
                    {String(item.id ?? i + 1).padStart(2, '0')}
                  </div>
                  <div className="misconception-risk-carousel-card-text">
                    {item.text}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="misconception-risk-carousel-arrow misconception-risk-carousel-arrow--next"
          onClick={next}
          aria-label="Next"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      <div className="misconception-risk-carousel-dots">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`misconception-risk-carousel-dot${
              i === active ? ' is-active' : ''
            }`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default React.memo(MisconceptionRiskCarousel);
